/**
 * WebSocket hook for Device App video calls
 * 
 * AUTO-CONNECT BEHAVIOR:
 * This hook automatically connects to the WebSocket when deviceId is provided
 * and disconnects on unmount or when deviceId changes. Callers do NOT need to
 * manually call connect/disconnect.
 * 
 * The hook does NOT export connect/disconnect functions - the connection is
 * managed internally based on the deviceId prop.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { acceptCall, rejectCall } from './callsApi.ts';

const WS_BASE = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://') || 'wss://api.hayatfamily.app';
const RECONNECT_DELAYS = [1000, 2000, 5000, 10000];

export interface DeviceCallState {
  callId: string;
  status: 'idle' | 'incoming' | 'in_call' | 'ended';
  fromUserName?: string;
  seniorName?: string;
  reason?: string;
}

/**
 * @param deviceId - Device ID for WebSocket connection (auto-connects when provided)
 * @returns Hook state and control functions (does NOT include connect/disconnect)
 */
export function useDeviceCallWebSocket(deviceId: string) {
  const [wsReady, setWsReady] = useState(false);
  const [currentCall, setCurrentCall] = useState<DeviceCallState | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttempt = useRef(0);
  const reconnectTimer = useRef<NodeJS.Timeout | null>(null);
  const deviceIdRef = useRef(deviceId);
  const webrtcSignalCallbackRef = useRef<((signal: any) => void) | null>(null);

  // Update deviceId ref when it changes
  useEffect(() => {
    deviceIdRef.current = deviceId;
  }, [deviceId]);

  /**
   * Send WebRTC signal to family
   */
  const sendSignal = useCallback((callId: string, signal: any) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.warn('[Device WS] Cannot send signal, WebSocket not ready');
      return;
    }
    
    const message = {
      type: 'webrtc.signal',
      payload: {
        call_id: callId,
        signal,
      },
    };
    
    console.log('[Device WS] Sending signal:', message);
    wsRef.current.send(JSON.stringify(message));
  }, []);

  /**
   * Handle incoming WebSocket messages
   */
  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const message = JSON.parse(event.data);
      console.log('[Device WS] Received:', message);
      
      switch (message.type) {
        case 'call.incoming': {
          const payload = message.payload;
          setCurrentCall({
            callId: payload.call_id,
            status: 'incoming',
            fromUserName: payload.from_user_name,
            seniorName: payload.senior_name,
          });
          break;
        }
        
        case 'call.ended': {
          const payload = message.payload;
          setCurrentCall((prev) =>
            prev?.callId === payload.call_id
              ? { ...prev, status: 'ended', reason: payload.reason }
              : prev
          );
          // Auto-clear after 3 seconds
          setTimeout(() => {
            setCurrentCall(null);
          }, 3000);
          break;
        }
        
        case 'call.accepted':
        case 'call.rejected':
          // These are mostly for family side, just log
          console.log('[Device WS] Call status update:', message.type);
          break;
        
        case 'webrtc.signal': {
          const payload = message.payload;
          console.log('[Device WS] WebRTC signal received:', payload.signal?.type);
          
          // Trigger callback if registered
          if (webrtcSignalCallbackRef.current && payload.signal) {
            webrtcSignalCallbackRef.current(payload.signal);
          }
          break;
        }
        
        default:
          console.log('[Device WS] Unknown message type:', message.type);
      }
    } catch (err) {
      console.error('[Device WS] Error handling message:', err);
    }
  }, []);

  /**
   * Connect to WebSocket
   */
  const connect = useCallback(() => {
    const currentDeviceId = deviceIdRef.current;
    
    if (!currentDeviceId) {
      console.warn('[Device WS] No device ID provided, skipping connection');
      return;
    }

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      console.log('[Device WS] Already connected');
      return;
    }
    
    const WS_URL = `${WS_BASE}/api/ws/call/device/${currentDeviceId}`;
    console.log('[Device WS] Connecting to:', WS_URL);
    
    try {
      const ws = new WebSocket(WS_URL);
      
      ws.onopen = () => {
        console.log('[Device WS] Connected');
        setWsReady(true);
        setError(null);
        reconnectAttempt.current = 0;
      };
      
      ws.onmessage = handleMessage;
      
      ws.onerror = (err) => {
        console.error('[Device WS] Error:', err);
        setError('WebSocket-Verbindung fehlgeschlagen');
      };
      
      ws.onclose = (event) => {
        console.log('[Device WS] Disconnected:', event.code, event.reason);
        setWsReady(false);
        
        // Attempt reconnect with backoff
        const delay = RECONNECT_DELAYS[Math.min(reconnectAttempt.current, RECONNECT_DELAYS.length - 1)];
        console.log(`[Device WS] Reconnecting in ${delay}ms...`);
        
        reconnectTimer.current = setTimeout(() => {
          reconnectAttempt.current++;
          connect();
        }, delay);
      };
      
      wsRef.current = ws;
    } catch (err) {
      console.error('[Device WS] Failed to create WebSocket:', err);
      setError('Anrufe nicht verfügbar');
    }
  }, [handleMessage]);

  /**
   * Disconnect WebSocket
   */
  const disconnect = useCallback(() => {
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
    
    if (wsRef.current) {
      console.log('[Device WS] Disconnecting');
      wsRef.current.close();
      wsRef.current = null;
    }
    
    setWsReady(false);
  }, []);

  /**
   * Accept incoming call
   */
  const acceptIncomingCall = useCallback(async (callId: string) => {
    const currentDeviceId = deviceIdRef.current;
    
    if (!currentDeviceId) {
      throw new Error('No device ID available');
    }

    try {
      console.log('[Device WS] Accepting call:', callId);
      await acceptCall(callId, currentDeviceId);
      
      setCurrentCall((prev) =>
        prev?.callId === callId
          ? { ...prev, status: 'in_call' }
          : prev
      );
    } catch (err) {
      console.error('[Device WS] Failed to accept call:', err);
      throw err;
    }
  }, []);

  /**
   * Reject incoming call
   */
  const rejectIncomingCall = useCallback(async (callId: string, reason: string = 'rejected') => {
    const currentDeviceId = deviceIdRef.current;
    
    if (!currentDeviceId) {
      throw new Error('No device ID available');
    }

    try {
      console.log('[Device WS] Rejecting call:', callId);
      await rejectCall(callId, currentDeviceId, reason);
      
      setCurrentCall((prev) =>
        prev?.callId === callId
          ? { ...prev, status: 'ended', reason }
          : prev
      );
      
      // Clear after delay
      setTimeout(() => setCurrentCall(null), 2000);
    } catch (err) {
      console.error('[Device WS] Failed to reject call:', err);
      throw err;
    }
  }, []);

  /**
   * Clear current call state
   */
  const clearCall = useCallback(() => {
    setCurrentCall(null);
  }, []);
  
  /**
   * Register callback for WebRTC signals
   */
  const onWebRTCSignal = useCallback((callback: ((signal: any) => void) | null) => {
    webrtcSignalCallbackRef.current = callback;
  }, []);

  // Auto-connect on mount or when deviceId changes
  useEffect(() => {
    if (deviceId) {
      connect();
    }
    
    return () => {
      disconnect();
    };
  }, [deviceId, connect, disconnect]);

  return {
    wsReady,
    currentCall,
    error,
    acceptIncomingCall,
    rejectIncomingCall,
    clearCall,
    sendSignal,
    onWebRTCSignal,
  };
}
